import GeoJSON from 'ol/format/GeoJSON';
import type Geometry from 'ol/geom/Geometry';

import type { GeoJsonFeatureCollectionDTO, GeoJsonFeatureDTO } from '@nora-monorepo/nora-api-client';
import { MapTile } from '@nora-monorepo/shared';

/**
 * The features of one draw layer within one tile, and the layer's features generation they
 * belong to.
 */
export interface CachedDrawFeatureTile {
  generation?: number;
  features: GeoJsonFeatureDTO[];
  /**
   * Sequence number of the load that fetched it; a later write from an older load is ignored.
   */
  loadSequence: number;
}

/**
 * A feature with its geometry read into OpenLayers, for tile intersection checks.
 */
interface ParsedDrawFeature {
  feature: GeoJsonFeatureDTO;
  geometry: Geometry;
}

/**
 * Default number of (draw layer, tile) entries kept before the least recently used are evicted.
 */
const DEFAULT_MAX_ENTRIES = 2000;

/**
 * In-memory LRU cache of draw layer features per map tile.
 *
 * Features are stored unclipped, so a feature crossing tile edges is cached in every tile it
 * intersects; collect() de-duplicates them by id. Cached feature objects are handed out as-is,
 * so an unchanged tile yields the same objects on every collect — the draw layer manager uses
 * that identity to skip re-reading unchanged features into OpenLayers.
 */
export class DrawFeatureTileCache {
  /**
   * Insertion order doubles as recency order: reads and writes move an entry to the end.
   */
  private entries = new Map<string, CachedDrawFeatureTile>();

  /**
   * Latest features generation the server reported per draw layer ID. Entries of an older
   * generation are outdated: their features may have changed since.
   */
  private knownGenerations = new Map<string, number>();

  /**
   * @param maxEntries Number of (draw layer, tile) entries kept before the least recently used are evicted
   */
  constructor(private readonly maxEntries = DEFAULT_MAX_ENTRIES) {}

  /**
   * Cache key of a (draw layer, tile) entry; prefixed by the draw layer ID for clearDrawLayer.
   */
  private static entryKey(drawLayerId: string, tile: MapTile): string {
    return `${drawLayerId}|${tile.key}`;
  }

  /**
   * Number of cached (draw layer, tile) entries.
   */
  get size(): number {
    return this.entries.size;
  }

  /**
   * The cached entry of a draw layer's tile, marking it as most recently used.
   */
  get(drawLayerId: string, tile: MapTile): CachedDrawFeatureTile | undefined {
    const key = DrawFeatureTileCache.entryKey(drawLayerId, tile);
    const entry = this.entries.get(key);

    if (entry) {
      this.entries.delete(key);
      this.entries.set(key, entry);
    }

    return entry;
  }

  /**
   * Stores a draw layer's tile as most recently used, evicting the least recently used
   * entries beyond the capacity. Ignored when the cached entry came from a newer load, so an
   * older overlapping response cannot replace it.
   */
  set(drawLayerId: string, tile: MapTile, entry: CachedDrawFeatureTile): void {
    const key = DrawFeatureTileCache.entryKey(drawLayerId, tile);
    const cached = this.entries.get(key);

    if (cached && cached.loadSequence > entry.loadSequence) {
      return;
    }

    this.entries.delete(key);
    this.entries.set(key, entry);

    for (const oldestKey of this.entries.keys()) {
      if (this.entries.size <= this.maxEntries) {
        break;
      }

      this.entries.delete(oldestKey);
    }
  }

  /**
   * The latest features generation the server reported for a draw layer, if any.
   */
  knownGeneration(drawLayerId: string): number | undefined {
    return this.knownGenerations.get(drawLayerId);
  }

  /**
   * Records a features generation the server reported for a draw layer. Generations only
   * increase, so an older one from an overlapping response is ignored.
   */
  setKnownGeneration(drawLayerId: string, generation: number): void {
    const knownGeneration = this.knownGenerations.get(drawLayerId);

    if (knownGeneration === undefined || generation > knownGeneration) {
      this.knownGenerations.set(drawLayerId, generation);
    }
  }

  /**
   * Whether every given draw layer has cached features for the tile at its known generation.
   */
  isUpToDate(tile: MapTile, drawLayerIds: string[]): boolean {
    return drawLayerIds.every((drawLayerId) => {
      const entry = this.entries.get(DrawFeatureTileCache.entryKey(drawLayerId, tile));

      return entry?.generation !== undefined && entry.generation === this.knownGenerations.get(drawLayerId);
    });
  }

  /**
   * An unbounded copy of the cached entries and known generations of the given tiles and draw
   * layers. A load revalidates and collects against its snapshot, so neither eviction nor a
   * concurrent load can change the entries it relies on before it collects its result.
   */
  snapshot(tiles: MapTile[], drawLayerIds: string[]): DrawFeatureTileCache {
    const snapshot = new DrawFeatureTileCache(Number.POSITIVE_INFINITY);

    for (const drawLayerId of drawLayerIds) {
      const knownGeneration = this.knownGenerations.get(drawLayerId);

      if (knownGeneration !== undefined) {
        snapshot.setKnownGeneration(drawLayerId, knownGeneration);
      }
    }

    for (const tile of tiles) {
      for (const drawLayerId of drawLayerIds) {
        const entry = this.get(drawLayerId, tile);

        if (entry) {
          snapshot.set(drawLayerId, tile, entry);
        }
      }
    }

    return snapshot;
  }

  /**
   * Replaces changed IDs in every cached tile, including offscreen tiles and other zooms,
   * and returns their replacements intersecting the current view's tiles.
   * Patched tiles have no known generation until their next full revalidation.
   */
  updateFeatures(drawLayerId: string, featureIds: string[], features: GeoJsonFeatureDTO[], visibleTiles: MapTile[]): GeoJsonFeatureDTO[] {
    const removedIds = new Set(featureIds);
    const formatter = new GeoJSON();
    const replacements = features.map(feature => ({ feature, geometry: formatter.readGeometry(feature.geometry) }));
    const prefix = `${drawLayerId}|`;

    for (const [key, entry] of this.entries) {
      if (key.startsWith(prefix)) {
        this.patchEntry(key, entry, removedIds, DrawFeatureTileCache.featuresIntersecting(
          replacements,
          [MapTile.fromKey(key.slice(prefix.length))],
        ));
      }
    }

    return DrawFeatureTileCache.featuresIntersecting(replacements, visibleTiles);
  }

  /**
   * Swaps the removed IDs of a cached entry for the added features, if anything changes.
   */
  private patchEntry(key: string, entry: CachedDrawFeatureTile, removedIds: Set<string>, added: GeoJsonFeatureDTO[]): void {
    const remaining = entry.features.filter(feature => !removedIds.has(feature.id));

    if (remaining.length === entry.features.length && added.length === 0) {
      return;
    }

    this.entries.set(key, { features: [...remaining, ...added], loadSequence: entry.loadSequence });
  }

  /**
   * The features whose geometry intersects any of the tiles.
   */
  private static featuresIntersecting(parsedFeatures: ParsedDrawFeature[], tiles: MapTile[]): GeoJsonFeatureDTO[] {
    const extents = tiles.map(tile => tile.getBBox().getGeoJsonBBox());

    return parsedFeatures
      .filter(({ geometry }) => extents.some(extent => geometry.intersectsExtent(extent)))
      .map(({ feature }) => feature);
  }

  /**
   * One FeatureCollection per draw layer from the cached tiles, de-duplicated by feature id.
   * Draw layers without cached tiles get an empty FeatureCollection.
   */
  collect(tiles: MapTile[], drawLayerIds: string[]): Record<string, GeoJsonFeatureCollectionDTO> {
    const collections: Record<string, GeoJsonFeatureCollectionDTO> = {};

    for (const drawLayerId of drawLayerIds) {
      collections[drawLayerId] = this.collectDrawLayer(tiles, drawLayerId);
    }

    return collections;
  }

  /**
   * One draw layer's FeatureCollection from the cached tiles, de-duplicated by feature id.
   */
  private collectDrawLayer(tiles: MapTile[], drawLayerId: string): GeoJsonFeatureCollectionDTO {
    const seenFeatureIds = new Set<string>();
    const features: GeoJsonFeatureDTO[] = [];

    for (const tile of tiles) {
      for (const feature of this.get(drawLayerId, tile)?.features ?? []) {
        if (!seenFeatureIds.has(feature.id)) {
          seenFeatureIds.add(feature.id);
          features.push(feature);
        }
      }
    }

    return { type: 'FeatureCollection', features };
  }

  /**
   * Removes every cached tile and the known generation of a draw layer.
   */
  clearDrawLayer(drawLayerId: string): void {
    const prefix = `${drawLayerId}|`;

    this.knownGenerations.delete(drawLayerId);

    for (const key of [...this.entries.keys()]) {
      if (key.startsWith(prefix)) {
        this.entries.delete(key);
      }
    }
  }

  /**
   * Removes every cached tile and known generation.
   */
  clear(): void {
    this.entries.clear();
    this.knownGenerations.clear();
  }
}
